/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/study.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { useQuery } from '@apollo/client/react';
import PanelService from '@components/central-monitoring/panels/add-panel/service';
import LogoAudit from '@pages/admin/audit-log/logo-audit';
import { StudyAuditFields } from 'queries/generated/fragments.generated';
import { getActiveModules } from 'utils/convertor';
import { GetMimeType } from 'views/components/core/edit-logo/image-service';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import Enums from 'views/pages/admin/audit-log/definitions/enums';
import { getFieldChangeItems, getProcessedFieldChangeItems } from 'views/pages/admin/audit-log/service';
import { useAuthorization, useLocalState, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { createArrayFromEnum } from '@utils/generator';
// @ts-expect-error @types collides with node_modules/@types
import { PanelType, RowDataSourceTable } from '@types/graphql-global-types';
import { GET_PANELS } from '@queries/central-monitoring';
import { getPanels, getPanelsVariables } from '@queries/generated/central-monitoring.generated';

const StudyRecordChangeGrid = ({ oldRecord, newRecord, oldItem, newItem, additionalInfo }: any) => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const { state } = useLocalState();
    const rowOrderTranslateValues = [
        'siteNameAsc',
        'siteNameDesc',
        'siteScoreDesc',
        'redKRIsDesc',
        'redDecisionKRIsDesc',
    ];

    const { data: panels } = useQuery<getPanels, getPanelsVariables>(GET_PANELS, {
        variables: {
            organisationId: user?.organisationId,
            studyId: state?.selectedStudy?.id ?? Constants.ObjectId.Empty,
            timestamp: newItem?.updated ?? '',
        },
        fetchPolicy: 'no-cache',
    });

    const getOldItems = (oldRecordItems: any, newRecordItems: any) => {
        oldRecordItems = oldRecordItems?.filter((item: any) => item !== null);
        newRecordItems = newRecordItems?.filter((item: any) => item !== null);

        // Align the number of elements to display all AuditLog rows
        if (newRecordItems?.length > oldRecordItems?.length) {
            const columns = oldRecordItems;
            const quantityDifference = newRecordItems.length - oldRecordItems.length;

            for (let i = 0; i < quantityDifference; i++) {
                columns.push({});
            }

            return columns;
        }

        return oldRecordItems;
    };

    const refactoredOldRecord = {
        ...oldRecord,
        // Setting these fields to their default values for the creation record or the default value if not set on
        // the changes records
        ScoreGreenThreshold: oldRecord.ScoreGreenThreshold
            ? oldRecord.ScoreGreenThreshold
            : newRecord.ScoreGreenThreshold
              ? 35
              : null,
        ScoreAmberThreshold: oldRecord.ScoreAmberThreshold
            ? oldRecord.ScoreAmberThreshold
            : newRecord.ScoreAmberThreshold
              ? 70
              : null,
        ScoreThreshold: oldRecord.ScoreThreshold ? oldRecord.ScoreThreshold : null,
        AdjustmentWeight: oldRecord.AdjustmentWeight ? oldRecord.AdjustmentWeight : null,
        SitesToExclude: null,
        RiskOverviewConfiguration: {
            ...oldRecord?.RiskOverviewConfiguration,
            SubjectField:
                oldRecord?.RiskOverviewConfiguration?.SubjectField &&
                oldRecord?.RiskOverviewConfiguration?.SubjectField.length
                    ? oldRecord?.RiskOverviewConfiguration?.SubjectField
                    : undefined,
        },
        StudyOverviewTableConfigurationColumns: getOldItems(
            oldRecord.StudyOverviewTableConfiguration?.ColumnConfigurations,
            newRecord.StudyOverviewTableConfiguration?.ColumnConfigurations,
        ),
        SiteOverviewTableConfigurationColumns: getOldItems(
            oldRecord.StudySiteOverviewTableConfiguration?.ColumnConfigurations,
            newRecord.StudySiteOverviewTableConfiguration?.ColumnConfigurations,
        ),
        StudyOverviewTableConfigurationPanels: getOldItems(
            oldRecord.StudyOverviewTableConfiguration?.PanelConfigurations,
            newRecord.StudyOverviewTableConfiguration?.PanelConfigurations,
        ),
        StudySiteOverviewTableConfigurationPanels: getOldItems(
            oldRecord.StudySiteOverviewTableConfiguration?.PanelConfigurations,
            newRecord.StudySiteOverviewTableConfiguration?.PanelConfigurations,
        ),
        RowOrder:
            typeof oldRecord.StudySiteOverviewTableConfiguration?.RowOrder === 'number'
                ? t(`admin:${rowOrderTranslateValues[oldRecord.StudySiteOverviewTableConfiguration?.RowOrder]}`)
                : '',
        StudySiteOverviewTableConfigurationColumns: '',
        SitesStatusesToExclude: oldRecord?.SitesStatusesToExclude?.length ? oldRecord.SitesStatusesToExclude : null,
        SitesStatusesToExcludeFromAutomation: oldRecord?.SitesStatusesToExcludeFromAutomation?.length
            ? oldRecord.SitesStatusesToExcludeFromAutomation
            : null,
        _Modules: oldRecord?.Modules > 0 ? getActiveModules(oldRecord?.Modules).split(', ') : [],
        Modules: null,
        CfId: oldRecord?.CfId?.$oid,
    };

    const refactoredNewRecord = {
        ...newRecord,
        ScoreGreenThreshold: oldItem ? newRecord.ScoreGreenThreshold : 35,
        ScoreAmberThreshold: oldItem ? newRecord.ScoreAmberThreshold : 70,
        SitesToExclude: null,
        RiskOverviewConfiguration: {
            ...newRecord.RiskOverviewConfiguration,
            SubjectField:
                newRecord.RiskOverviewConfiguration.SubjectField &&
                newRecord.RiskOverviewConfiguration.SubjectField.length
                    ? newRecord.RiskOverviewConfiguration.SubjectField
                    : undefined,
        },
        StudyOverviewTableConfigurationColumns: newRecord.StudyOverviewTableConfiguration?.ColumnConfigurations?.filter(
            (item: any) => item !== null,
        ),
        SiteOverviewTableConfigurationColumns:
            newRecord.StudySiteOverviewTableConfiguration?.ColumnConfigurations?.filter((item: any) => item !== null),
        StudyOverviewTableConfigurationPanels: newRecord.StudyOverviewTableConfiguration?.PanelConfigurations?.filter(
            (item: any) => item !== null,
        ),
        StudySiteOverviewTableConfigurationPanels:
            newRecord.StudySiteOverviewTableConfiguration?.PanelConfigurations?.filter((item: any) => item !== null),
        RowOrder:
            typeof newRecord.StudySiteOverviewTableConfiguration?.RowOrder === 'number'
                ? t(`admin:${rowOrderTranslateValues[newRecord.StudySiteOverviewTableConfiguration?.RowOrder]}`)
                : '',
        SitesStatusesToExclude: newRecord?.SitesStatusesToExclude?.length ? newRecord.SitesStatusesToExclude : null,
        SitesStatusesToExcludeFromAutomation: newRecord?.SitesStatusesToExcludeFromAutomation?.length
            ? newRecord.SitesStatusesToExcludeFromAutomation
            : null,
        _Modules: newRecord?.Modules > 0 ? getActiveModules(newRecord?.Modules).split(', ') : [],
        Modules: null,
        CfId: newRecord?.CfId?.$oid,
    };

    delete refactoredOldRecord.StudyOverviewTableConfiguration;
    delete refactoredOldRecord.StudySiteOverviewTableConfiguration;
    delete refactoredNewRecord.StudyOverviewTableConfiguration;
    delete refactoredNewRecord.StudySiteOverviewTableConfiguration;

    const refactoredOldItem = {
        ...oldItem,
        _modules: oldItem?.modules > 0 ? getActiveModules(oldItem?.modules).split(', ') : [],
    };

    const refactoredNewItem = {
        ...newItem,
        _modules: newItem?.modules > 0 ? getActiveModules(newItem?.modules).split(', ') : [],
        evaluationModel: newItem?.evaluationModel?.name ? { ...newItem?.evaluationModel } : null,
    };

    const enums: any = {
        SvtDependency: Enums.SvtDependency,
        StudyAutomationColor: Enums.StudyAutomationColor,
        ControlsAutomationColor: Enums.ControlsAutomationColor,
        CreationMethod: Enums.StudyCreationMethod,
        StudyState: Enums.StudyState,
    };

    const lookups: any = {
        EvaluationModelId: (study: StudyAuditFields) => study?.evaluationModel?.name,
        Logo: (study: StudyAuditFields, recordData: any) => {
            const base64 = recordData?.Logo?.$binary?.base64 as string | undefined;

            if (base64 == null || base64.length === 0) {
                return 'None';
            }

            const mimeType = GetMimeType(base64);
            if (mimeType == null) {
                return base64;
            }

            return `data:${mimeType};base64,${base64}`;
        },
        LogoId: (_: StudyAuditFields, recordData: any) => <LogoAudit logoId={recordData?.LogoId?.$oid} />,
        SitesStatusesToExclude: (study: StudyAuditFields) => study?.sitesStatusesToExclude?.join(', '),
        SitesStatusesToExcludeFromAutomation: (study: StudyAuditFields) =>
            study?.sitesStatusesToExcludeFromAutomation?.join(', '),
    };

    // Associations:
    const associations: any = {
        _Modules: {
            itemField: '_modules',
            name: 'Module',
            nameResolver: (moduleName: any) => moduleName,
        },
        SitesStatusesToExclude: {
            itemField: 'sitesStatusesToExclude',
            name: 'Excluded Site States',
            nameResolver: (status: any) => status,
        },
        SitesStatusesToExcludeFromAutomation: {
            itemField: 'sitesStatusesToExcludeFromAutomation',
            name: 'Site Statuses',
            nameResolver: (status: any) => status,
        },
        Programs: {
            itemField: 'programs',
            name: 'Programs',
            nameResolver: (program: any) => program?.name,
        },
        RiskLibraryCategoryIds: {
            itemField: 'riskLibraryCategories',
            name: 'Risk Library Categories',
            nameResolver: (category: any) => category?.name,
        },
        Products: {
            itemField: 'products',
            name: 'Product',
            nameResolver: (product: any) => product?.name,
        },
        Indications: {
            itemField: 'indications',
            name: 'Indication',
            nameResolver: (indication: any) => indication?.name,
        },
        StudyFunctionIds: {
            itemField: 'studyFunction',
            name: 'Study Functions',
            nameResolver: (functionItem: any) => functionItem?.name,
        },
    };

    const listOfObjects = {
        RiskOverviewConfiguration: [
            'AmberDecisionKris',
            'AmberKris',
            'NumSubjects',
            'RedDecisionKris',
            'RedKris',
            'SiteActiveDate',
            'SiteCountry',
            'SiteMonitor',
            'SiteStatus',
            'SubjectField',
        ],
        StudySummaryConfiguration: [
            'NumCompleted',
            'NumEnrolled',
            'NumRandomised',
            'NumScreenFailed',
            'NumScreened',
            'NumStudyDiscontinued',
            'NumTreatmentDiscontinued',
        ],
        SiteSummaryConfiguration: [
            'ActionChart',
            'Monitor',
            'NumCompleted',
            'NumEnrolled',
            'NumRandomised',
            'NumScreenFailed',
            'NumScreened',
            'NumStudyDiscontinued',
            'NumTreatmentDiscontinued',
            'ObservationChart',
            'SiteActiveDate',
            'SiteCountry',
            'SiteStatus',
        ],
        CtqScreenWidgetsConfiguration: [
            'NumberTotal',
            'NumberActive',
            'NumberActiveCritical',
            'NumberActiveCtQsWithoutRisks',
            'NumberActiveCtQsWithoutFunctionalDataReviews',
        ],
    };

    const getColumnValue = (recordData: any) => {
        const getColumnValueForType = (fieldValue: number) => {
            if (fieldValue === 1) return 'Calculated';
            if (fieldValue === 2) return 'Overview';
            return 'Static';
        };

        const getColumnValueForDataSource = (fieldValue: any) => {
            const dataSourceTableType = createArrayFromEnum(Enums.DataSourceTableType, '_TABLE', null).filter(
                (itemEnum: any) => itemEnum.id !== RowDataSourceTable.NONE,
            );
            return dataSourceTableType[fieldValue]?.text || t('ra:n/a');
        };

        let value = '';

        Object.keys(recordData).forEach((property) => {
            const fieldValue = recordData[property];

            if (property === 'Type') {
                value += `${property}: ${getColumnValueForType(fieldValue)}; `;
            } else if (property === 'DataSource') {
                value += `${property}: ${getColumnValueForDataSource(fieldValue)}; `;
            } else if (property !== 'Default') {
                value += `${property}: ${fieldValue || t('ra:n/a')}; `;
            }
        });

        return value.replace(/;\s*$/, '');
    };

    const panelItems =
        panels?.panels?.map((panel) => {
            const model = PanelService.getPanelModel(panel?.modelParams);
            return {
                plotTitle: panel?.graph?.plotTitle,
                model: panel?.panelType === PanelType.MODEL ? t(`cm:${model}`) : '',
                active: panel?.active,
                panelId: panel?.id,
            };
        }) || [];

    const getPanelValues = (recordData: any) => {
        let value = '';

        if (typeof recordData === 'object' && Object.keys(recordData || {}).length && panelItems.length) {
            const panelId = recordData?.PanelId?.$oid;
            const currentPanel = panelItems?.find((panel: any) => panel?.panelId === panelId);
            const panelFields = {
                plotTitle: currentPanel?.plotTitle,
                model: currentPanel?.model,
                order: recordData?.PanelOrder,
                // active: currentPanel?.active, // Maybe need to display later
            };

            Object.keys(panelFields).forEach((property) => {
                const fieldValue = panelFields[property];

                value += `${t(`auditLog:field-${property}`)}: ${fieldValue || t('ra:n/a')}; `;
            });

            return value.replace(/;\s*$/, '');
        }

        return value;
    };

    const arrayOfObjects = {
        StudyOverviewTableConfigurationColumns: {
            valueResolver: getColumnValue,
        },
        SiteOverviewTableConfigurationColumns: {
            valueResolver: getColumnValue,
        },
        StudyOverviewTableConfigurationPanels: {
            valueResolver: getPanelValues,
        },
        StudySiteOverviewTableConfigurationPanels: {
            valueResolver: getPanelValues,
        },
    };
    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem: refactoredOldItem,
            newItem: refactoredNewItem,
            field,
            lookups,
            associations,
            enums,
            listOfObjects,
            arrayOfObjects,
        }),
    );

    return <FieldChangeGrid items={items} />;
};

export { StudyRecordChangeGrid };
